// src/components/BookCover/BookCover3D.jsx
// ─────────────────────────────────────────────────────────────────
// CSS 3D book preview with proper box geometry, page edges, and
// full drag-to-rotate in all directions.
// Accepts print specs to adjust visual rendering.
// ─────────────────────────────────────────────────────────────────
import React, { useState, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';

// Book size → real canvas pixel dimensions
const BOOK_SIZE_DIMS = {
  half_letter:  { w: 360, h: 540 },
  trade_6x9:   { w: 396, h: 594 },
  a5:          { w: 348, h: 492 },
  pocket_4x7:  { w: 280, h: 462 },
  us_letter:   { w: 528, h: 684 },
};

// Binding type → spine depth override
const BINDING_DEPTHS = {
  perfect:      100,
  case_bound:   110,
  coil:         60,
  wire:         55,
  saddle_stitch: 12,
};

// Paper type → page-edge colours
const PAPER_EDGE_COLORS = {
  bond_75:       { a: '#faf6ee', b: '#e8e2d4' },   // warm white
  bond_90:       { a: '#fdfcf8', b: '#ebe6da' },   // brighter white
  cream_offset:  { a: '#f5eed6', b: '#dfd5b8' },   // creamy
  newsprint:     { a: '#eee8d7', b: '#d5cdb8' },   // yellowish
  coated_gloss:  { a: '#fdfdfd', b: '#e6e6e6' },   // near-white
  coated_matte:  { a: '#fafafa', b: '#e2e2e2' },   // neutral grey-white
};

// Lamination → CSS filter for front/back face
const LAMINATION_FILTER = {
  none:       'none',
  gloss:      'contrast(1.05) saturate(1.12)',
  matte:      'contrast(0.97) saturate(0.92) brightness(1.02)',
  soft_touch: 'contrast(0.98) saturate(0.95) brightness(1.01)',
};

const BookCover3D = forwardRef(function BookCover3D({
  frontImg, spineImg, backImg,
  bookSize, paperType, lamination, bindingType, coverType,
  spineW,  // live spine width in canvas pixels — overrides BINDING_DEPTHS lookup
  zoom = 1, // external zoom from parent
}, ref) {
  const [rotY, setRotY] = useState(-25);
  const [rotX, setRotX] = useState(8);
  const dragging = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  // Expose rotate/reset controls to parent so they can be rendered outside the scroll container
  useImperativeHandle(ref, () => ({
    rotateLeft:  () => setRotY(p => p - 90),
    rotateRight: () => setRotY(p => p + 90),
    resetView:   () => { setRotY(-25); setRotX(8); },
  }), []);

  const onDown = useCallback((e) => {
    dragging.current = true;
    last.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = e.clientX - last.current.x;
    const dy = e.clientY - last.current.y;
    last.current = { x: e.clientX, y: e.clientY };
    setRotY(p => p + dx * 0.6);
    setRotX(p => Math.max(-60, Math.min(60, p - dy * 0.4)));
  }, []);

  const onUp = useCallback(() => { dragging.current = false; }, []);

  // ── Compute dynamic dimensions from print specs ──
  const { W, H, D, pageGradient, coverFilter } = useMemo(() => {
    const sizeDims = BOOK_SIZE_DIMS[bookSize] || BOOK_SIZE_DIMS.half_letter;
    // Use live spineW from canvas if provided, otherwise fall back to binding-type default
    const depth = spineW != null ? spineW : (BINDING_DEPTHS[bindingType] || BINDING_DEPTHS.perfect);
    const edgeColors = PAPER_EDGE_COLORS[paperType] || PAPER_EDGE_COLORS.bond_75;
    const filter = LAMINATION_FILTER[lamination] || LAMINATION_FILTER.none;

    return {
      W: sizeDims.w * zoom,
      H: sizeDims.h * zoom,
      D: depth * zoom,
      pageGradient: `repeating-linear-gradient(to bottom, ${edgeColors.a} 0px, ${edgeColors.a} 1px, ${edgeColors.b} 1px, ${edgeColors.b} 2px)`,
      coverFilter: filter,
    };
  }, [bookSize, bindingType, paperType, lamination, spineW, zoom]);

  // Hardcover adds a visible board edge effect
  const isHardcover = coverType === 'hardcover' || coverType === 'hardcover_jacket';
  const coverBorder = isHardcover ? '2px solid rgba(80,60,40,0.35)' : 'none';

  /*
   * Box coordinate system — origin at centre of the box (W × H × D).
   *
   * Face            Size     Normal   Transform
   * ─────────────── ──────── ──────── ──────────────────────────────────
   * Front (cover)   W × H    +Z       translateZ(D/2)
   * Back            W × H    −Z       rotateY(180°) translateZ(D/2)
   * Spine (left)    D × H    −X       rotateY(−90°) translateZ(W/2)
   * Pages (right)   D × H    +X       rotateY(90°)  translateZ(W/2)
   * Top             W × D    +Y       rotateX(90°)  translateZ(H/2)
   * Bottom          W × D    −Y       rotateX(−90°) translateZ(H/2)
   */

  return (
    <div className="book3d-container">
      <div
        className="book3d-scene"
        style={{ minHeight: Math.ceil(H + D) + 60 }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      >
        <div
          className="book3d-box"
          style={{
            width: W,
            height: H,
            transform: `rotateX(${rotX}deg) rotateY(${rotY}deg)`,
            transition: dragging.current ? 'none' : 'transform 0.12s ease-out',
          }}
        >
          {/* ── Front cover ── */}
          <div className="book3d-face" style={{
            width: W, height: H,
            backgroundImage: frontImg ? `url(${frontImg})` : undefined,
            backgroundSize: '100% 100%',
            transform: `translateZ(${D / 2}px)`,
            borderRadius: '0 3px 3px 0',
            filter: coverFilter,
            border: coverBorder,
          }} />

          {/* ── Back cover ── */}
          <div className="book3d-face" style={{
            width: W, height: H,
            backgroundImage: backImg ? `url(${backImg})` : undefined,
            backgroundSize: '100% 100%',
            transform: `rotateY(180deg) translateZ(${D / 2}px)`,
            borderRadius: '3px 0 0 3px',
            filter: coverFilter,
            border: coverBorder,
          }} />

          {/* ── Spine (left edge) ── */}
          <div className="book3d-face" style={{
            width: D, height: H,
            backgroundImage: spineImg ? `url(${spineImg})` : undefined,
            backgroundSize: '100% 100%',
            transform: `rotateY(-90deg) translateZ(${W / 2}px)`,
            left: (W - D) / 2,
          }} />

          {/* ── Page edges (right side) ── */}
          <div className="book3d-face" style={{
            width: D, height: H,
            background: pageGradient,
            transform: `rotateY(90deg) translateZ(${W / 2}px)`,
            left: (W - D) / 2,
            boxShadow: 'inset 0 0 8px rgba(0,0,0,0.08)',
          }} />

          {/* ── Top edge (pages) ── */}
          <div className="book3d-face" style={{
            width: W, height: D,
            background: pageGradient,
            transform: `rotateX(90deg) translateZ(${H / 2}px)`,
            top: (H - D) / 2,
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.06)',
          }} />

          {/* ── Bottom edge (pages) ── */}
          <div className="book3d-face" style={{
            width: W, height: D,
            background: pageGradient,
            transform: `rotateX(-90deg) translateZ(${H / 2}px)`,
            top: (H - D) / 2,
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.06)',
          }} />
        </div>
      </div>
    </div>
  );
});

export default BookCover3D;
